import { useRouteLoaderDataTyped } from "infrastructure/router";
import type { ResourceResponse } from "types";
import { ApiRouteTypes } from "consts/enpoints/api";
import { BooleanSwitchValue } from "consts/general";
import { ECommerceOnboardingApplicationStatus } from "consts/ecommerce";
import { when } from "util/support";
import type { OnboardingApplication } from "./types";

export const useOnboardingApplication = (routeId = ApiRouteTypes.GetOnboardingsApplications) => {
    const response = useRouteLoaderDataTyped<ResourceResponse<OnboardingApplication>>(routeId);

    const getData = (fallback: unknown) => response?.data ?? fallback;

    const getStatus = () => getData({}).status;

    const getReasons = (fallback: unknown) => getData({}).reasons ?? fallback;

    const hasStatus = (...statuses: Array<ECommerceOnboardingApplicationStatus>) => statuses.includes(getStatus());

    const isApplicationStarted = () => Boolean(getData(null));

    const isApplicationSubmitted = () => Boolean(getData({}).hasAgreedTermsConditions);

    const isApplicationIncomplete = () => when(
        response.data,
        () => Object.entries(response.data)
            .some(([key, value]) => (
                when(
                    key.startsWith('isIncomplete'),
                    (value !== BooleanSwitchValue.Off)
                )
            )),
        true
    );

    const isApplicationHasRequestedChange = () => (
        Object.entries(getData({}))
            .some(([key, value]) => (
                when(
                    key.startsWith('hasRequestedChange'),
                    (value !== BooleanSwitchValue.Off)
                )
            ))
    );

    const isApplicationCompleted = () => (
        !isApplicationIncomplete() &&
        !isApplicationHasRequestedChange()
    );

    return {
        response,
        isApplicationStarted,
        isApplicationSubmitted,
        isApplicationCompleted,
        isApplicationHasRequestedChange,
        hasStatus,
        getReasons
    };
};

export default useOnboardingApplication;
